import { forwardRef, type ReactElement, type ReactNode } from 'react'

import { DrawerPreview as DrawerPrimitive } from '@base-ui/react/drawer'
import { tv, type VariantProps } from 'tailwind-variants'

import { cn } from '../../../utils/cn'
import { Button } from '../../atoms/button/button'
import { Icon } from '../../atoms/icon/icon'
import { Text } from '../../atoms/text/text'

/* Slide-in mirrors animate-ui's Radix Sheet: motion's spring(stiffness 150,
 * damping 22) baked into a static linear() curve (the accordion's approach),
 * driving translate and opacity together. Reduced motion falls back to a
 * plain fade. */
const popupVariants = tv({
  base: [
    'bg-surface shadow-drawer fixed bottom-0 right-0 top-0 z-50 flex flex-col overflow-hidden',
    'outline-none',
    'motion-safe:transition-[translate,opacity] motion-safe:duration-700 motion-safe:ease-[linear(0,0.0516,0.1671,0.3055,0.4431,0.5674,0.6732,0.7593,0.8271,0.8789,0.9174,0.9454,0.9652,0.9789,0.9881,0.994,0.9977,1,1.001,1.0015,1.0016,1.0015,1.0014,1.0011,1)]',
    'motion-reduce:transition-opacity motion-reduce:duration-300 motion-reduce:ease-out',
    'data-[ending-style]:translate-x-full data-[starting-style]:translate-x-full',
    'data-[ending-style]:opacity-0 data-[starting-style]:opacity-0'
  ],
  variants: {
    size: {
      sm: 'w-full max-w-md',
      md: 'w-full max-w-[640px]',
      lg: 'w-full max-w-2xl',
      xl: 'w-full max-w-3xl'
    }
  },
  defaultVariants: { size: 'md' }
})

// Padding lives on this inner wrapper, NOT on the scroll container
// (drawer-scroll), so that `padding-bottom` is included in the scrollable
// extent. When the padded container is also the overflow-scroll container,
// browsers do not extend scroll-height by `padding-bottom` in flex-column
// layouts — so the bottom padding looks fine when content fits, but is
// "eaten" the moment content overflows. Putting padding on a child
// contributes to the child's height, which is naturally scrollable.
const bodyVariants = tv({
  base: 'flex min-h-full flex-col',
  variants: {
    padding: {
      none: '',
      md: 'px-8 py-10'
    }
  },
  defaultVariants: { padding: 'md' }
})

// 40px / radius-10 per the Ømni comp; interim composition over Button's
// frozen 24px `icon` alias until the Ømni Button (iconOnly) lands.
const defaultCloseButton = (
  <Button
    type='button'
    variant='tertiary'
    size='icon'
    className='absolute right-8 top-10 z-10 size-10 rounded-lg border-transparent'
  >
    <Icon name='close' className='size-5 text-[20px]' />
  </Button>
)

const backdropClasses = cn(
  'bg-dialog-bg fixed inset-0 z-40',
  'transition-opacity duration-200 ease-out motion-reduce:transition-none',
  'data-[ending-style]:opacity-0 data-[starting-style]:opacity-0'
)

export type DrawerSize = NonNullable<VariantProps<typeof popupVariants>['size']>
export type DrawerPadding = NonNullable<
  VariantProps<typeof bodyVariants>['padding']
>

export type DrawerContentProps = Omit<
  DrawerPrimitive.Popup.Props,
  'children'
> & {
  size?: DrawerSize
  /**
   * Inner spacing. Defaults to `md` (`px-8 py-10`, the Ømni spec). `none` is
   * the single escape for flush content (tables, custom layouts). Applied to
   * an inner wrapper, not the scroll container itself — this keeps
   * `padding-bottom` visible when the drawer content overflows and scrolls.
   * `className` on `DrawerContent` is forwarded to the outer popup.
   */
  padding?: DrawerPadding
  hideCloseButton?: boolean
  closeAriaLabel?: string
  /**
   * Override the default close button. The provided element is used as the
   * `render` target of the underlying close primitive, so open/close behavior
   * and `aria-label` are wired up automatically. Position the element yourself
   * (the default is absolutely positioned in the top-right).
   */
  closeButton?: ReactElement
  /** Optional header pinned above the scrollable body. */
  header?: ReactNode
  /** Layout classes for the body, such as a full-height table container. */
  bodyClassName?: string
  /**
   * Optional fixed footer (action buttons, typically), pinned below the
   * scrollable body with the drawer footer chrome.
   */
  footer?: ReactNode
  children?: ReactNode
}

function Drawer(props: DrawerPrimitive.Root.Props) {
  return <DrawerPrimitive.Root {...props} />
}

function DrawerTrigger(props: DrawerPrimitive.Trigger.Props) {
  return <DrawerPrimitive.Trigger data-slot='drawer-trigger' {...props} />
}

function DrawerTitle({
  className,
  children,
  ...props
}: DrawerPrimitive.Title.Props) {
  return (
    <DrawerPrimitive.Title
      data-slot='drawer-title'
      render={
        <Text
          as='h2'
          variant='h4'
          weight='medium'
          className={cn('text-surface-title-50', className)}
        />
      }
      {...props}
    >
      {children}
    </DrawerPrimitive.Title>
  )
}

function DrawerDescription({
  className,
  children,
  ...props
}: DrawerPrimitive.Description.Props) {
  return (
    <DrawerPrimitive.Description
      data-slot='drawer-description'
      render={
        <Text
          as='p'
          variant='title-lg'
          weight='regular'
          className={cn('text-surface-body', className)}
        />
      }
      {...props}
    >
      {children}
    </DrawerPrimitive.Description>
  )
}

function DrawerClose(props: DrawerPrimitive.Close.Props) {
  return <DrawerPrimitive.Close data-slot='drawer-close' {...props} />
}

const DrawerContent = forwardRef<HTMLDivElement, DrawerContentProps>(
  function DrawerContent(
    {
      size = 'md',
      padding = 'md',
      hideCloseButton = false,
      closeAriaLabel = 'Close',
      closeButton,
      header,
      bodyClassName,
      footer,
      className,
      children,
      ...props
    },
    ref
  ) {
    return (
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Backdrop className={backdropClasses} />
        <DrawerPrimitive.Popup
          ref={ref}
          data-slot='drawer-content'
          className={cn(popupVariants({ size }), className)}
          {...props}
        >
          {!hideCloseButton && (
            <DrawerPrimitive.Close
              data-slot='drawer-close'
              aria-label={closeAriaLabel}
              render={closeButton ?? defaultCloseButton}
            />
          )}
          {header && (
            <div data-slot='drawer-header' className='shrink-0'>
              {header}
            </div>
          )}
          <div data-slot='drawer-scroll' className='flex-1 overflow-y-auto'>
            <div
              data-slot='drawer-body'
              className={cn(bodyVariants({ padding }), bodyClassName)}
            >
              {children}
            </div>
          </div>
          {footer && (
            <div
              data-slot='drawer-footer'
              className='border-surface-200 flex min-h-[66px] shrink-0 items-center gap-2 border-t px-8'
            >
              {footer}
            </div>
          )}
        </DrawerPrimitive.Popup>
      </DrawerPrimitive.Portal>
    )
  }
)

export {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger
}
